import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CallIcon from "@mui/icons-material/Call";
import imageCompression from 'browser-image-compression';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Backdrop,
  Fade,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Paper
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { QRCodeCanvas } from "qrcode.react";
import {
  getBatchDetails,
  updateBatchStatus,
  completeOrder,
  uploadPaymentProof,
  getUpiDetails, cancelOrderByDelivery
} from "../api/deliveryApi";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ReportOrderDialog from "./ReportOrderDialog";


export default function BatchDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  // 🔥 PAYMENT STATE
  const [openPayment, setOpenPayment] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // 🚨 REPORT / CANCEL
  const [openCancel, setOpenCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancelInput, setShowCancelInput] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [selectedCancelOrder, setSelectedCancelOrder] = useState(null);
  const [paymentImage, setPaymentImage] = useState(null);
  
  // 🔄 LOADING STATES
  const [uploading, setUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false); // Used for Payments/Cancels
  const [statusLoading, setStatusLoading] = useState(false); // Used for local Status updates
  const [loadingText, setLoadingText] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [upiData, setUpiData] = useState(null);

  const fetchData = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) {
        setActionLoading(true);
        setLoadingText("Refreshing batch data...");
      }

      const res = await getBatchDetails(id);
      setData(res.data);

    } catch (err) {
      alert("Failed to load batch");
    } finally {
      if (showLoader) {
        setActionLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (status, orderId = null) => {
    try {
      setStatusLoading(true); // TRIGGER LOCAL SPINNER

      await updateBatchStatus({
        batch_id: id,
        status,
        order_id: orderId
      });

      await fetchData(false); // SILENT REFRESH

    } catch (err) {
      alert("Failed to update status");
    } finally {
      setStatusLoading(false); // REMOVE SPINNER
    }
  };

  const openMap = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  // 🔥 OPEN PAYMENT
  const handleDeliverClick = async (order) => {
    try {
      setActionLoading(true);
      setLoadingText("Loading payment page...");
      setSelectedOrder(order);
      const res = await getUpiDetails(order.order_id);
      setUpiData(res.data);
      setActionLoading(false);
      setOpenPayment(true);
    } catch (err) {
      setActionLoading(false);
      alert("Failed to load payment");
    }
  };

  // 🚨 OPEN REPORT
  const handleOpenCancel = (order) => {
    setSelectedCancelOrder(order);
    setCancelReason("");
    setShowCancelInput(false);
    setOpenCancel(true);
  };

  // 🚨 CANCEL ORDER
  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      alert("Please enter cancel reason");
      return;
    }

    try {
      setActionLoading(true);
      setLoadingText("Cancelling order...");

      await cancelOrderByDelivery({
        order_id: selectedCancelOrder.order_id,
        reason: cancelReason
      });

      setOpenCancel(false);
      setConfirmCancelOpen(false);
      setCancelReason("");

      const updated = await getBatchDetails(id);

      const allCompleted =
        updated.data.orders.every(
          (o) =>
            o.status === "DELIVERED" ||
            o.status === "CANCELLED"
        );

      if (allCompleted) {
        navigate("/dashboard");
      } else {
        setData(updated.data);
      }

    } catch (err) {
      alert(
        err.response?.data?.error ||
        "Cancel failed"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // 🔥 SUBMIT PAYMENT
  const handleSubmitPayment = async () => {
    try {
      setActionLoading(true);
      setLoadingText("Processing payment...");

      let paymentProofUrl = note || "";

      // =========================
      // ✅ UPI IMAGE UPLOAD
      // =========================
      if (method === "UPI") {
        if (!paymentImage) {
          setActionLoading(false);
          alert("Upload payment screenshot");
          return;
        }
        setLoadingText("Uploading payment proof...");
        setUploading(true);
        const formData = new FormData();
        formData.append("file", paymentImage);
        const uploadRes = await uploadPaymentProof(formData);
        paymentProofUrl = uploadRes.data.url;
        setUploading(false);
      }

      // =========================
      // ✅ COMPLETE ORDER
      // =========================
      setLoadingText("Completing delivery...");

      const completeRes = await completeOrder({
        order_id: selectedOrder.order_id,
        payment_method: method,
        payment_proof: paymentProofUrl,
        amount: selectedOrder.final_amount
      });

      if (completeRes.data?.success === false) {
        throw new Error(completeRes.data.error || "Payment could not be recorded");
      }

      await updateStatus(
        "DELIVERED",
        selectedOrder.order_id
      );

      setOpenPayment(false);
      setMethod("");
      setNote("");
      setPaymentImage(null);
      setActionLoading(false);
      setSuccessOpen(true);

      setTimeout(async () => {
        setSuccessOpen(false);

        const updated = await getBatchDetails(id);

        const allCompleted =
          updated.data.orders.every(
            (o) =>
              o.status === "DELIVERED" ||
              o.status === "CANCELLED"
          );

        if (allCompleted) {
          navigate("/dashboard");
        } else {
          setData(updated.data);
        }
      }, 2000);

    } catch (err) {
      setUploading(false);
      setActionLoading(false);
      alert(
        err.response?.data?.error ||
        "Payment failed"
      );
    }
  };

  if (!data) {
    return (
      <Backdrop
        open={true}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          backdropFilter: "blur(8px)",
          background: "rgba(15, 23, 42, 0.6)",
          flexDirection: "column",
          gap: 3
        }}
      >
        <CircularProgress
          size={70}
          thickness={4}
          sx={{ color: "#38bdf8" }}
        />
        <Typography
          sx={{
            fontWeight: "700",
            fontSize: "1.2rem",
            letterSpacing: "1px",
            textShadow: "0 2px 4px rgba(0,0,0,0.5)"
          }}
        >
          Loading batch details...
        </Typography>
      </Backdrop>
    );
  }

  const batchStatus = data?.status;
  const isOutForDelivery = batchStatus === "OUT_FOR_DELIVERY";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
        pt: { xs: 10, sm: 12 }, 
        pb: { xs: 10, sm: 12 }, 
        px: { xs: 2, sm: 3 },   
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "800px" }}>

        {/* ========================= */}
        {/* 🔥 HEADER SECTION (RESPONSIVE WHITE) */}
        {/* ========================= */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 }, // 👈 Scaled padding for mobile
            borderRadius: "24px",
            background: "#ffffff",
            color: "#1e293b",
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.05)",
            border: "1px solid #f1f5f9",
            mb: { xs: 3, sm: 4 },
            position: "relative",
            overflow: "hidden"
          }}
        >
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, background: 'radial-gradient(circle, rgba(255,107,107,0.04) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />
          <Box sx={{ position: 'absolute', bottom: -50, left: -20, width: 150, height: 150, background: 'radial-gradient(circle, rgba(255,142,83,0.04) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            
            {/* TOP ROW: Icon, Title, and Chip */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    width: { xs: 38, sm: 44 }, 
                    height: { xs: 38, sm: 44 },
                    background: "#f8fafc", color: "#475569",
                    border: "1px solid #e2e8f0", "&:hover": { background: "#e2e8f0" },
                    flexShrink: 0
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: { xs: 16, sm: 18 }, ml: "2px" }} />
                </IconButton>
                <Typography 
                  sx={{ 
                    fontWeight: 900, 
                    letterSpacing: "-0.5px", 
                    color: "#0f172a", 
                    fontSize: "clamp(1.4rem, 6vw, 2.2rem)", // 👈 Fluid size scales perfectly for 6-digit IDs
                    lineHeight: 1.1, 
                    wordBreak: "break-word" 
                  }}
                >
                  Batch #{id}
                </Typography>
              </Box>
              <Chip
                label={data.status.replace("_", " ")}
                sx={{
                  background: "#f1f5f9", color: "#334155", fontWeight: "800", 
                  fontSize: { xs: "0.7rem", sm: "0.75rem" }, 
                  letterSpacing: "0.5px", border: "1px solid #e2e8f0",
                  mt: { xs: 0.5, sm: 1 }
                }}
              />
            </Box>

            {/* MIDDLE ROW: Description */}
            <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
              <Typography sx={{ color: "#64748b", fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.95rem" }, lineHeight: 1.4 }}>
                Manage your batch deliveries and update statuses on the go.
              </Typography>
            </Box>

            {/* 🔥 STATUS BUTTONS - PROGRESSIVE FLOW */}
            <Box sx={{ mt: 1 }}>
              {batchStatus === "ASSIGNED" && (
                <Button
                  variant="contained"
                  onClick={() => updateStatus("AT_VENDOR")}
                  disabled={statusLoading}
                  fullWidth
                  sx={{
                    borderRadius: "14px", background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)", color: "white",
                    "&:hover": { background: "linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)" }, py: 1.6, 
                    boxShadow: "0 8px 20px rgba(255, 107, 107, 0.25)", fontWeight: "800", fontSize: "1.05rem", textTransform: "none",
                    "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" }
                  }}
                >
                  {statusLoading ? <CircularProgress size={24} color="inherit" /> : "🏪 Reached Vendor"}
                </Button>
              )}

              {batchStatus === "AT_VENDOR" && (
                <Button
                  variant="contained"
                  onClick={() => updateStatus("PICKED_UP")}
                  disabled={statusLoading}
                  fullWidth
                  sx={{
                    borderRadius: "14px", background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)", color: "white",
                    "&:hover": { background: "linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)" }, py: 1.6, 
                    boxShadow: "0 8px 20px rgba(255, 107, 107, 0.25)", fontWeight: "800", fontSize: "1.05rem", textTransform: "none",
                    "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" }
                  }}
                >
                  {statusLoading ? <CircularProgress size={24} color="inherit" /> : "🛍️ Picked Up"}
                </Button>
              )}

              {batchStatus === "PICKED_UP" && (
                <Button
                  variant="contained"
                  onClick={() => updateStatus("OUT_FOR_DELIVERY")}
                  disabled={statusLoading}
                  fullWidth
                  sx={{
                    borderRadius: "14px", background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)", color: "white",
                    "&:hover": { background: "linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)" }, py: 1.6, 
                    boxShadow: "0 8px 20px rgba(255, 107, 107, 0.25)", fontWeight: "800", fontSize: "1.05rem", textTransform: "none",
                    "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" }
                  }}
                >
                  {statusLoading ? <CircularProgress size={24} color="inherit" /> : "🛵 Start Delivery"}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
        <Typography variant="h6" fontWeight="700" sx={{ color: "#37474f", mb: 2, ml: 1 }}>
          📦 Active Orders ({data.orders.length})
        </Typography>

        {/* ========================= */}
        {/* 🔥 ORDERS LIST */}
        {/* ========================= */}
        <Stack spacing={3}>
          {data.orders.map((o) => (
            <Card
              key={o.order_id}
              sx={{
                borderRadius: "20px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
                border: "1px solid #edf2f7",
                overflow: "visible"
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>

                {/* Order Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="800"
                    sx={{ color: "#1a202c" }}
                  >
                    Order #{o.order_id}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1
                    }}
                  >
                    <Chip
                      label={o.status.replace("_", " ")}
                      size="small"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        background:
                          o.status === "DELIVERED"
                            ? "#def7ec"
                            : o.status === "CANCELLED"
                              ? "#ffebee"
                              : "#e1effe",

                        color:
                          o.status === "DELIVERED"
                            ? "#03543f"
                            : o.status === "CANCELLED"
                              ? "#d32f2f"
                              : "#1e429f",
                      }}
                    />

                    {isOutForDelivery &&
                      o.status !== "DELIVERED" &&
                      o.status !== "CANCELLED" && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<ReportProblemOutlinedIcon />}
                          onClick={() => handleOpenCancel(o)}
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: "700",
                            minWidth: "unset",
                            px: 1.5
                          }}
                        >
                          Report
                        </Button>
                      )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

                {/* Customer Details */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                  
                  {/* 👈 MODIFIED: Added wrapper with space-between for the call button */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box sx={{ fontSize: "1.2rem", mt: 0.2 }}>👤</Box>
                      <Box>
                        <Typography fontWeight="700" sx={{ color: "#2d3748" }}>{o.user?.name}</Typography>
                        <Typography variant="body2" sx={{ color: "#718096" }}>{o.user?.phone}</Typography>
                      </Box>
                    </Box>

                    {/* 👈 NEW: Quick Call Button */}
                    {o.user?.phone && (
                      <IconButton
                        component="a"
                        href={`tel:${o.user.phone}`}
                        sx={{
                          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", // Emerald green for call
                          color: "white",
                          boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
                          width: 42,
                          height: 42,
                          "&:hover": { background: "linear-gradient(135deg, #059669 0%, #047857 100%)" }
                        }}
                      >
                        <CallIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", background: "#f8fafc", p: 1.5, borderRadius: "12px" }}>
                    <Box sx={{ fontSize: "1.2rem", mt: 0.2 }}>📍</Box>
                    <Typography variant="body2" sx={{ color: "#4a5568", lineHeight: 1.5 }}>
                      {o.address?.flat}, {o.address?.building}, {o.address?.landmark}
                    </Typography>
                  </Box>
                </Box>

                {/* Items List */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="700" sx={{ color: "#a0aec0", textTransform: "uppercase", letterSpacing: "1px", mb: 1 }}>
                    Items
                  </Typography>
                  <Stack spacing={1}>
                    {o.items.map((i, idx) => (
                      <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight="500" sx={{ color: "#4a5568" }}>• {i.name}</Typography>
                        <Chip label={`x${i.qty}`} size="small" sx={{ height: "24px", fontSize: "0.7rem", fontWeight: "bold" }} />
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {/* Actions */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderRadius: "12px",
                      py: 1.5,
                      fontWeight: "bold",
                      borderColor: "#cbd5e1",
                      color: "#475569",
                      "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" }
                    }}
                    onClick={() => openMap(o.lat, o.lng)}
                  >
                    🧭 Navigate
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!isOutForDelivery || o.status === "DELIVERED" || o.status === "CANCELLED" || actionLoading}
                    sx={{
                      borderRadius: "12px",
                      py: 1.5,
                      fontWeight: "bold",
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
                      "&:hover": { background: "linear-gradient(135deg, #059669 0%, #047857 100%)" },
                      opacity: !isOutForDelivery || o.status === "DELIVERED" || o.status === "CANCELLED" ? 0.5 : 1,
                      filter: !isOutForDelivery || o.status === "DELIVERED" || o.status === "CANCELLED" ? "blur(0.5px)" : "none"
                    }}
                    onClick={() => handleDeliverClick(o)}
                  >
                    {actionLoading && selectedOrder?.order_id === o.order_id ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : o.status === "DELIVERED" ? (
                      "✅ Delivered"
                    ) : o.status === "CANCELLED" ? (
                      "❌ Cancelled"
                    ) : (
                      "✅ Deliver Order"
                    )}
                  </Button>
                </Stack>

              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* ========================= */}
        {/* 💳 PAYMENT DIALOG */}
        {/* ========================= */}
        <Dialog
          open={openPayment}
          onClose={
            actionLoading
              ? undefined
              : () => setOpenPayment(false)
          }
          fullScreen={isMobile}
          slotProps={{
            paper: {
              sx: {
                borderRadius: { xs: 0, sm: "24px" },
                padding: { xs: "1px 0px", sm: "16px" },
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
              }
            }
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: "800",
              color: "#1a237e",
              fontSize: { xs: "1.2rem", sm: "1.5rem" }
            }}
          >
            Complete Payment
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  sx={{
                    px: 2.5,
                    borderRadius: "12px",
                    background: "rgba(30, 136, 229, 0.08)",
                    border: "1px solid rgba(30, 136, 229, 0.15)",
                    color: "#1565c0",
                    fontWeight: "700",
                    fontSize: { xs: "0.95rem", sm: "1rem" }
                  }}
                >
                  Order ID: #{selectedOrder?.order_id}
                </Typography>
              </Box>
            </Box>

            {/* AMOUNT */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#78909c",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontWeight: "bold"
                }}
              >
                Amount to Collect
              </Typography>
              <Typography
                sx={{
                  fontWeight: "900",
                  color: "#2e7d32",
                  fontSize: { xs: "2.5rem", sm: "3rem" },
                  lineHeight: 1.2
                }}
              >
                ₹{upiData?.amount || selectedOrder?.final_amount}
              </Typography>
            </Box>

            {/* PAYMENT METHOD */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mb: 2 }}>
              <Button
                fullWidth
                variant={method === "COD" ? "contained" : "outlined"}
                onClick={() => setMethod("COD")}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  fontWeight: "bold",
                  fontSize: "1.1rem"
                }}
              >
                💵 Cash
              </Button>
              <Button
                fullWidth
                variant={method === "UPI" ? "contained" : "outlined"}
                onClick={() => setMethod("UPI")}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  fontWeight: "bold",
                  fontSize: "1.1rem"
                }}
              >
                📱 UPI
              </Button>
            </Box>

            {/* COD */}
            <Fade in={method === "COD"} mountOnEnter unmountOnExit>
              <Box sx={{ mt: 5 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Payment Note (e.g. Paid ₹250)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      minHeight: "6rem"
                    }
                  }}
                />
              </Box>
            </Fade>

            {/* UPI */}
            <Fade in={method === "UPI"} mountOnEnter unmountOnExit>
              <Box>
                {upiData ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      background: "white",
                      p: { xs: 1, sm: 2 },
                      borderRadius: "16px",
                      border: "2px dashed #cfd8dc"
                    }}
                  >
                    <QRCodeCanvas
                      value={upiData.upi_url}
                      size={220}
                      level="H"
                      includeMargin
                    />
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontWeight: "700",
                        color: "#37474f",
                        fontSize: { xs: "1rem", sm: "1.1rem" }
                      }}
                    >
                      Scan & Pay ₹{upiData.amount}
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Button
                        component="label"
                        variant="outlined"
                        fullWidth
                        sx={{ borderRadius: "12px", py: 1.5, fontWeight: "bold", mt: 2 }}
                      >
                        📸 Upload Payment Photo
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            try {
                              // Compress the image down to ~500KB maximum
                              const options = {
                                maxSizeMB: 0.5, 
                                maxWidthOrHeight: 1024,
                                useWebWorker: true,
                              };
                              
                              const compressedFile = await imageCompression(file, options);
                              
                              // Pass the compressed, lightweight file to the state
                              setPaymentImage(compressedFile);
                            } catch (error) {
                              console.error("Compression error:", error);
                              alert("Failed to compress image. Please try again.");
                            }
                          }}
                        />
                      </Button>
                      {paymentImage && (
                        <Typography sx={{ mt: 1, fontSize: "0.9rem", color: "success.main" }}>
                          ✅ {paymentImage.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            </Fade>
          </DialogContent>

          <DialogActions 
            sx={{ 
              justifyContent: "space-between", // 👈 Pushes buttons to opposite ends
              px: { xs: 3, sm: 4 },            // 👈 Increased horizontal padding
              pb: { xs: 3, sm: 4 },            // 👈 Increased bottom padding
              pt: 2 
            }}
          >
            <Button
              onClick={() => setOpenPayment(false)}
              sx={{ 
                fontWeight: "600", 
                color: "#ef4444", // 👈 Red color matching the image
                fontSize: "0.95rem",
                letterSpacing: "0.5px"
              }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitPayment}
              disabled={!method || (method === "UPI" && !paymentImage) || uploading || actionLoading}
              sx={{
                borderRadius: "24px", // 👈 Pill-shaped radius matching the image
                px: 4,
                py: 1,
                fontWeight: "700",
                fontSize: "0.95rem",
                letterSpacing: "0.5px",
                background: method ? "#1976d2" : "#e2e8f0", // 👈 Solid blue from image
                color: method ? "#ffffff" : "#94a3b8",
                boxShadow: method ? "0 4px 14px rgba(25, 118, 210, 0.3)" : "none",
                "&:hover": { background: method ? "#1565c0" : "#e2e8f0" },
                "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8" } // Disabled state styles
              }}
            >
              {actionLoading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : uploading ? (
                "UPLOADING..."
              ) : (
                "CONFIRM"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ========================= */}
        {/* ✅ DELIVERY SUCCESS DIALOG */}
        {/* ========================= */}
        <Dialog
          open={successOpen}
          maxWidth="xs"
          fullWidth
          slotProps={{
            paper: {
              sx: { borderRadius: "24px" }
            }
          }}
        >
          <DialogContent sx={{ textAlign: "center", py: 5 }}>
            <Box
              sx={{
                width: 100, height: 100, mx: "auto", borderRadius: "50%",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "3.5rem", color: "white", mb: 3, boxShadow: "0 10px 25px rgba(16, 185, 129, 0.4)"
              }}
            >
              ✓
            </Box>
            <Typography sx={{ fontWeight: "900", fontSize: "1.75rem", color: "#064e3b", mb: 1 }}>
              Delivery Successful!
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "1.1rem" }}>
              Great job. Updating system...
            </Typography>
          </DialogContent>
        </Dialog>

        <ReportOrderDialog
          open={openCancel}
          onClose={() => setOpenCancel(false)}
          cancelReason={cancelReason}
          setCancelReason={setCancelReason}
          showCancelInput={showCancelInput}
          setShowCancelInput={setShowCancelInput}
          confirmCancelOpen={confirmCancelOpen}
          setConfirmCancelOpen={setConfirmCancelOpen}
          handleCancelOrder={handleCancelOrder}
          actionLoading={actionLoading}
        />

        {/* 🔥 GLOBAL ACTION LOADER (Used for Payment & Cancellation Processing) */}
        <Backdrop
          open={actionLoading}
          sx={{
            color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 9999, backdropFilter: "blur(8px)",
            background: "rgba(15, 23, 42, 0.6)", flexDirection: "column", gap: 3
          }}
        >
          <CircularProgress size={70} thickness={4} sx={{ color: "#38bdf8" }} />
          <Typography sx={{ fontWeight: "700", fontSize: "1.2rem", letterSpacing: "1px", textShadow: "0 2px 4px rgba(0,0,0,0.5)" }}>
            {loadingText || "Please wait..."}
          </Typography>
        </Backdrop>

      </Box>
    </Box>
  );
}